import { HttpClient, HttpHeaders, HttpContext } from '@angular/common/http';
import * as i0 from '@angular/core';
import { InjectionToken, inject, DestroyRef, Injectable } from '@angular/core';
import { WEB_DATA_SERVICES_CONFIG, ServicesBaseUrl } from '@garmin-avcloud/avcloud-web-data-services/config';
import { of, switchMap, fromEvent, first, map, finalize, forkJoin, defer, from, tap, catchError } from 'rxjs';
import { DOCUMENT } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Reader, load } from 'protobufjs';
import { OMIT_CONTENT_TYPE_HEADER, OMIT_ACCEPT_HEADER } from '@garmin-avcloud/avcloud-web-utils';
var ProtobufMessageType = /*#__PURE__*/function (ProtobufMessageType) {
  ProtobufMessageType["Decimal"] = "common.Decimal";
  ProtobufMessageType["FlightLogRow"] = "flightdatalog.Row";
  return ProtobufMessageType;
}(ProtobufMessageType || {});
// This should match the flight-log-row.proto message type

const PROTOBUF_PARSER_SERVICE_CONFIG_TOKEN = new InjectionToken('ProtobufParserServiceConfig');
let ProtobufParserService = /*#__PURE__*/(() => {
  class ProtobufParserService {
    constructor() {
      this.config = inject(PROTOBUF_PARSER_SERVICE_CONFIG_TOKEN);
      this.document = inject(DOCUMENT);
      this.destroyRef = inject(DestroyRef);
    }
    /**
     * Decodes a Uint8Array of serialized aircraft log row data into an JSON array.
     *
     * @param data The serialized data to decode
     * @returns Decoded array of FlightLogRow objects
     */
    decodeLogRows(data) {
      return (this.root == null ? this.initialize() : of(null)).pipe(switchMap(() => {
        if (this.root == null) {
          throw new Error('Unable to decode protobuf due to failed initialization');
        }
        const flightLogRowMessageType = this.root.lookupType(ProtobufMessageType.FlightLogRow);
        const fieldNamesWithDecimalObject = flightLogRowMessageType.fieldsArray.filter(field => field.type === ProtobufMessageType.Decimal).map(field => field.name);
        const fieldNamesToFix = flightLogRowMessageType.fieldsArray.filter(field => new RegExp(/\_\d/).test(field.name)).map(field => field.name);
        const reader = Reader.create(data);
        const unprocessedRows = [];
        // Each iteration of this loop represents a row in the logbook file
        while (reader.pos < reader.len) {
          const message = flightLogRowMessageType.decodeDelimited(reader);
          const rowObject = flightLogRowMessageType.toObject(message);
          unprocessedRows.push(rowObject);
        }
        const processedRows = [];
        const maxWorkerCount = this.document.defaultView?.navigator.hardwareConcurrency ?? 0;
        const arrays = this.splitArrayIntoSubArrays(unprocessedRows, maxWorkerCount);
        arrays.forEach(array => {
          const worker = new Worker(this.config.webWorkerPath);
          processedRows.push(fromEvent(worker, 'message').pipe(first(), map(message => message.data), finalize(() => worker.terminate()), takeUntilDestroyed(this.destroyRef)));
          worker.postMessage([array, fieldNamesWithDecimalObject, fieldNamesToFix]);
        });
        return forkJoin(processedRows).pipe(map(array => array.flat()));
      }));
    }
    initialize() {
      return defer(() => from(load(this.config.protoBundlePath))).pipe(tap(root => this.root = root), map(() => null), catchError(() => {
        throw new Error('Unable to initialize protobuf parser service with the provided proto bundle path');
      }));
    }
    splitArrayIntoSubArrays(array, subArrayCount) {
      if (subArrayCount < 2) {
        return [array];
      }
      const arrayOfSubArrays = [];
      const subArrayLength = Math.ceil(array.length / subArrayCount);
      let currentIndex = 0;
      while (currentIndex < array.length) {
        arrayOfSubArrays.push(array.slice(currentIndex, currentIndex += subArrayLength));
      }
      return arrayOfSubArrays;
    }
    static {
      this.ɵfac = function ProtobufParserService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ProtobufParserService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: ProtobufParserService,
        factory: ProtobufParserService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return ProtobufParserService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let AircraftFlightLogService = /*#__PURE__*/(() => {
  class AircraftFlightLogService {
    constructor() {
      this.http = inject(HttpClient);
      this.config = inject(WEB_DATA_SERVICES_CONFIG);
      this.baseUrl = `${ServicesBaseUrl[this.config.environment]}/aircraftFlightLogService/flight-logs`;
      this.protobufParserService = inject(ProtobufParserService);
    }
    getFlightLogsByAircraft(searchQuery) {
      return this.http.post(`${this.baseUrl}/by-aircraft`, searchQuery);
    }
    getFlightLog(id) {
      return this.http.get(`${this.baseUrl}/${id}`);
    }
    getFlightLogFile(id) {
      return this.http.get(`${this.baseUrl}/${id}/file`, {
        responseType: 'arraybuffer'
      }).pipe(switchMap(arrayBuffer => this.protobufParserService.decodeLogRows(new Uint8Array(arrayBuffer))));
    }
    getFlightLogCsv(id) {
      return this.http.get(`${this.baseUrl}/${id}/csv`, {
        responseType: 'blob'
      });
    }
    static {
      this.ɵfac = function AircraftFlightLogService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AircraftFlightLogService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: AircraftFlightLogService,
        factory: AircraftFlightLogService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return AircraftFlightLogService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let AircraftFlightLogUploadService = /*#__PURE__*/(() => {
  class AircraftFlightLogUploadService {
    constructor() {
      this.http = inject(HttpClient);
      this.config = inject(WEB_DATA_SERVICES_CONFIG);
      this.baseUrl = `${ServicesBaseUrl[this.config.environment]}/aircraftDataDataLake/v2/aircraft-data`;
    }
    uploadAircraftData(file) {
      const requestHeaders = new HttpHeaders({
        'Content-Type': 'application/octet-stream'
      });
      const requestContext = new HttpContext().set(OMIT_CONTENT_TYPE_HEADER, true);
      return this.http.post(`${this.baseUrl}/opaque`, file, {
        headers: requestHeaders,
        context: requestContext
      });
    }
    getUploadStatus(dataUuid) {
      return this.http.get(`${this.baseUrl}/${dataUuid}/status`);
    }
    getLogFile(dataUuid) {
      const requestHeaders = new HttpHeaders().set('Accept', 'application/octet-stream').set('Content-Type', 'application/octet-stream');
      const requestContext = new HttpContext().set(OMIT_ACCEPT_HEADER, true).set(OMIT_CONTENT_TYPE_HEADER, true);
      return this.http.get(`${this.baseUrl}/${dataUuid}`, {
        headers: requestHeaders,
        context: requestContext,
        responseType: 'blob'
      });
    }
    static {
      this.ɵfac = function AircraftFlightLogUploadService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AircraftFlightLogUploadService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: AircraftFlightLogUploadService,
        factory: AircraftFlightLogUploadService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return AircraftFlightLogUploadService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { AircraftFlightLogService, AircraftFlightLogUploadService, PROTOBUF_PARSER_SERVICE_CONFIG_TOKEN, ProtobufMessageType };
